// src/components/Modals/CurateQuestionModal.js
import React, { useState, useEffect } from 'react';

const ThemedCloseIcon = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M5 5L15 15M15 5L5 15"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const CurateQuestionModal = ({
  onClose,
  onQuestionCreated,
  currentUser,
  isArchive,
  useProfileTheme = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [question, setQuestion] = useState('');
  const [description, setDescription] = useState('');
  const [answer, setAnswer] = useState('');
  const [source, setSource] = useState('');
  const [showOnProfile, setShowOnProfile] = useState(true); // Checkbox checked by default
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // -- NEW TAGS STATE --
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState('')

  // Allows you to delete a tag by its index
  const removeTag = idx => {
   setTags(tags.filter((_, i) => i !== idx));
  };

  // Fade in modal shortly after mounting.
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose(), 600);
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('modal')) {
      handleClose();
    }
  };

  // When user types a tag and hits Enter
  const handleTagKeyDown = e => {
    // on Enter **or** Spacebar
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const t = tagInput.trim().toLowerCase();
      if (t && !tags.includes(t)) {
        setTags([...tags, t]);
      }
      setTagInput('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields.
    if (!question.trim()) {
      setError('Question title is required.');
      return;
    }
    if (!answer.trim()) {
      setError('Answer is required.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    // Prepare payload with current user's info.
    const payload = {
      question,
      detail: description,
      answer,
      source,
      displayOnProfile: showOnProfile,
      isCurated: true, // Marks this as a curated question.
      tip: 0, // Tip is zero for curated questions.
      yourName: currentUser.name,
      yourEmail: currentUser.email,
      toUsername: currentUser.username,
      fromUsername: currentUser.username,
      tags,
    };

    try {
      const res = await fetch('/api/questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Failed to create curated question.');
      } else {
        if (onQuestionCreated) onQuestionCreated(data);
        handleClose();
      }
    } catch (err) {
      setError('Failed to create curated question. Please try again.');
      console.error(err);
    }
    setIsSubmitting(false);
  };

  return (
    <div
      className={`modal${useProfileTheme ? ' modal-profile-theme' : ''} ${visible ? 'show' : ''}`}
      id="curate-question-modal"
      onClick={handleOverlayClick}
    >
      <div className="card modal-card" onClick={(e) => e.stopPropagation()}>
        <span className="close-modal" onClick={handleClose}>
          {useProfileTheme ? <ThemedCloseIcon /> : <img src="/images/i_modal_close.svg" alt="Close" />}
        </span>
        <form onSubmit={handleSubmit}>
          <div className="text-uppercase">
            Question Title <span className="red">*</span>
          </div>
          <input
            type="text"
            name="question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            required
          />
          <div className="text-uppercase margin-top-l">
            Description
          </div>
          <textarea
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="text-uppercase margin-top-l">
            Answer <span className="red">*</span>
          </div>
          <textarea
            name="answer"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            required
          />
          {/* -- TAGS INPUT UI -- */}
          <div className="text-uppercase margin-top-l">Tags</div>
          <div className="tags-input">
            {tags.map((t,i) => (
              <span key={i} className="tag">
                {t}
                <button type="button" className="remove-tag" onClick={() => removeTag(i)}>×</button>
              </span>
            ))}
            <input
              className="tag-input"
              type="text"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
            />
          </div>
          {isArchive && (
           <>
             <div className="text-uppercase margin-top-l">Source</div>
             <input
               type="text"
               name="source"
               value={source}
               onChange={(e) => setSource(e.target.value)}
             />
           </>
          )}
          <div className="modal-footer">
            <div className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                id="showOnProfile"
                type="checkbox"
                checked={showOnProfile}
                onChange={(e) => {
                  console.log("Checkbox changed, new value:", e.target.checked);
                  setShowOnProfile(e.target.checked);
                }}
                aria-labelledby="showOnProfileText"
              />
              {/* This text is no longer a label, so clicking it won’t toggle */}
              <p id="showOnProfileText" className="m-0">Show on profile</p>
            </div>
            {error && <p className="fail">{error}</p>}
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CurateQuestionModal;
